import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useSearchParams } from "react-router-dom";
import { Sun, Moon, ShoppingBag, MapPin, CalendarDays, Users, User, Bell, ChevronDown, Menu } from "lucide-react";
import { api } from "@/lib/api";
import { PlayerProvider, useAsvTheme, useCart, usePlayer } from "@/lib/asv";
import { CartDrawer } from "@/components/asv/CartDrawer";
import { MobileMenu } from "@/components/asv/MobileMenu";
import { Footer, Brand, assetUrl } from "@/components/asv/SiteFooter";
import { useAuth, isAdmin } from "@/context/AuthContext";
import "@/styles/asv.css";

const NAV = [["/venues", "Venues"], ["/events", "Events"], ["/games", "Games"], ["/sport-centres", "Sport Centres"]];
const TABS = [["/venues", "Venues", MapPin], ["/events", "Events", CalendarDays], ["/games", "Games", Users]];

const JoinMenu = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button onClick={() => setOpen(true)} onMouseEnter={() => setOpen(true)} className="flex items-center gap-1 text-sm font-semibold" data-testid="asv-join-menu">Join Us<ChevronDown className="h-3.5 w-3.5" /></button>
      {open && (
        <div className="asv-card absolute right-0 top-full z-50 mt-0 w-52 p-2 shadow-xl">
          <Link to="/join/user" onClick={() => setOpen(false)} className="block rounded-xl px-3 py-2 text-sm hover:bg-black/5" data-testid="asv-join-user">As a User</Link>
          <Link to="/join/vendor" onClick={() => setOpen(false)} className="block rounded-xl px-3 py-2 text-sm hover:bg-black/5" data-testid="asv-join-vendor">As a Vendor</Link>
        </div>
      )}
    </div>
  );
};

function Header({ dark, setDark, openCart, site }) {
  const { cart } = useCart();
  const { player, unread } = usePlayer();
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-40 border-b asv-line backdrop-blur-xl" style={{ background: "color-mix(in srgb, var(--asv-bg) 82%, transparent)" }}>
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4 sm:px-6">
        <Link to="/" className="shrink-0 text-lg sm:text-xl" data-testid="asv-logo"><Brand site={site} dark={dark} /></Link>
        <nav className="hidden items-center gap-6 md:flex">
          {NAV.map(([to, l]) => <NavLink key={to} to={to} className={({ isActive }) => `text-sm font-semibold ${isActive ? "asv-accent" : ""}`} data-testid={`asv-nav-${l.toLowerCase()}`}>{l}</NavLink>)}
          <JoinMenu />
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setDark(!dark)} className="asv-btn-ghost !p-2.5" aria-label="Toggle theme" data-testid="asv-theme-toggle">{dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</button>
          <button onClick={openCart} className="asv-btn-ghost relative !p-2.5" aria-label="Cart" data-testid="asv-cart-button"><ShoppingBag className="h-4 w-4" />
            {!!cart.items.length && <span className="asv-accent-bg absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full px-1 text-[10px] font-bold" data-testid="asv-cart-count">{cart.items.length}</span>}</button>
          {player ? (
            <Link to="/account" className="asv-btn-ghost relative" data-testid="asv-account-link"><User className="h-4 w-4" /><span className="hidden sm:inline">{player.name.split(" ")[0]}</span>
              {!!unread && <Bell className="h-3.5 w-3.5 asv-accent" data-testid="asv-unread" />}</Link>
          ) : isAdmin(user) ? <Link to="/platform" className="asv-btn" data-testid="asv-admin-link"><User className="h-4 w-4" /><span className="hidden sm:inline">Admin</span></Link>
          : player === null && user === false && <Link to="/signin" className="asv-btn" data-testid="asv-signin-link">Sign in</Link>}
        </div>
      </div>
    </header>
  );
}

const TAB = "relative flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold";
function TabBar({ openCart, openMenu, menuOpen }) {
  const { cart } = useCart();
  const { unread } = usePlayer();
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t asv-line pb-[env(safe-area-inset-bottom)] md:hidden" style={{ background: "var(--asv-surface)" }} data-testid="asv-tabbar">
      {TABS.map(([to, l, Icon]) => (
        <NavLink key={to} to={to} className={({ isActive }) => `${TAB} ${isActive && !menuOpen ? "asv-accent" : "asv-muted"}`} data-testid={`asv-tab-${l.toLowerCase()}`}><Icon className="h-5 w-5" />{l}</NavLink>
      ))}
      <button type="button" onClick={openCart} className={`${TAB} asv-muted`} data-testid="asv-tab-cart"><ShoppingBag className="h-5 w-5" />Cart
        {!!cart.items.length && <span className="asv-accent-bg absolute left-1/2 top-1 ml-1.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[9px] font-bold" data-testid="asv-tab-cart-count">{cart.items.length}</span>}</button>
      <button type="button" onClick={openMenu} className={`${TAB} ${menuOpen ? "asv-accent" : "asv-muted"}`} data-testid="asv-tab-menu"><Menu className="h-5 w-5" />Menu
        {!!unread && <span className="asv-accent-bg absolute left-1/2 top-1.5 ml-1.5 h-2 w-2 rounded-full" data-testid="asv-tab-menu-dot" />}</button>
    </nav>
  );
}

function applyAsvHead(icon) {
  document.querySelectorAll("link[rel='icon'], link[rel='apple-touch-icon']").forEach((l) => { l.href = icon ? assetUrl(icon) : "/asv-icon-192.png"; });
  let link = document.querySelector("link[rel='manifest']");
  if (!link) { link = document.createElement("link"); link.rel = "manifest"; document.head.appendChild(link); }
  link.href = `${process.env.REACT_APP_BACKEND_URL}/api/asv/manifest.webmanifest`;
}

function Shell() {
  const [dark, setDark] = useAsvTheme();
  const [cartOpen, setCartOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accent, setAccent] = useState("#16a34a");
  const [site, setSite] = useState(null);
  const [sp] = useSearchParams();
  const loc = useLocation();
  useEffect(() => { api.get("/asv/config").then((r) => { setAccent(r.data.highlight); setSite(r.data.site); applyAsvHead(r.data.site?.icon_url); }).catch(() => applyAsvHead()); }, []);
  useEffect(() => { if (sp.get("cart") === "1") setCartOpen(true); }, [sp]);
  useEffect(() => { window.scrollTo(0, 0); setMenuOpen(false); }, [loc.pathname]);
  return (
    <div className={`asv ${dark ? "asv-dark" : ""} pb-20 md:pb-0`} style={{ "--asv-accent": accent }} data-testid="asv-shell">
      <Header dark={dark} setDark={setDark} openCart={() => setCartOpen(true)} site={site} />
      <main className="mx-auto max-w-7xl px-4 sm:px-6"><Outlet context={{ openCart: () => setCartOpen(true) }} /></main>
      <Footer site={site} dark={dark} />
      <TabBar openCart={() => { setMenuOpen(false); setCartOpen(true); }} openMenu={() => setMenuOpen(true)} menuOpen={menuOpen} />
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} dark={dark} setDark={setDark} />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}

export default function AsvLayout() {
  return <PlayerProvider><Shell /></PlayerProvider>;
}
